/**
 * Execution Cockpit V6 Primitive Components
 *
 * Ported from: docs/ui-concepts/homepage-cockpit/helix-execution-cockpit-v6.jsx
 *
 * EXECUTIVE READABILITY PASS:
 * - Body text: 14-15px for decision-grade content
 * - Labels/secondary: 12-13px minimum
 * - Micro text: 11px (minimum for any text)
 * - Line height: 1.5 for comfortable reading
 */

'use client';

import React from 'react';
import {
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Gauge,
  CircleDot,
} from 'lucide-react';
import {
  c,
  ragColors,
  severityColors,
  urgencyColors,
  confidenceColors,
} from './tokens';
import type {
  ExecutionCockpitDailyBriefRagStatusDto,
  ExecutionCockpitDailyBriefConfidenceDto,
  ExecutionCockpitDailyBriefDecisionDto,
  ExecutionCockpitDailyBriefBetDto,
  ExecutionCockpitDailyBriefPriorityDriverDto,
  ExecutionCockpitDailyBriefProofDto,
  ExecutionCockpitDailyBriefOptionDto,
  ExecutionCockpitDailyBriefRiskTileDto,
} from '@helix/lib';
import { formatExecutiveAccountabilityLabel } from '@/lib/executive-safe-format';

const sanitizeExternalUrl = (value: string | null | undefined): string | null => {
  if (!value) return null;

  const raw = value.trim();
  if (!raw) return null;

  try {
    const parsed = new URL(raw);
    const protocol = parsed.protocol.toLowerCase();
    if (protocol !== 'http:' && protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// RAG DOT
// ═══════════════════════════════════════════════════════════════════════════
interface RAGDotProps {
  status: ExecutionCockpitDailyBriefRagStatusDto;
  size?: number;
}

export const RAGDot: React.FC<RAGDotProps> = ({ status, size = 10 }) => {
  const color = ragColors[status]?.dot ?? c.textMuted;
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// DELTA BADGE
// ═══════════════════════════════════════════════════════════════════════════
interface DeltaBadgeProps {
  value: number | null;
}

export const DeltaBadge: React.FC<DeltaBadgeProps> = ({ value }) => {
  if (value == null) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: 11,
          fontWeight: 600,
          color: c.textMuted,
        }}
      >
        —
      </span>
    );
  }
  if (value === 0) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          fontSize: 11,
          fontWeight: 600,
          color: c.textMuted,
        }}
      >
        <Minus style={{ width: 10, height: 10 }} />0
      </span>
    );
  }
  const isUp = value > 0;
  const color = isUp ? c.red : c.green;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        fontSize: 11,
        fontWeight: 600,
        color,
      }}
    >
      {isUp ? (
        <ArrowUpRight style={{ width: 11, height: 11 }} />
      ) : (
        <ArrowDownRight style={{ width: 11, height: 11 }} />
      )}
      {isUp ? '+' : ''}
      {value}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CONFIDENCE BADGE
// ═══════════════════════════════════════════════════════════════════════════
interface ConfidenceBadgeProps {
  level: ExecutionCockpitDailyBriefConfidenceDto;
  reason?: string;
}

export const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({ level, reason }) => {
  const config = confidenceColors[level] ?? confidenceColors.medium;
  return (
    <span
      title={reason}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 11,
        fontWeight: 500,
        color: config.color,
        padding: '2px 6px',
        backgroundColor: config.bg,
        border: `1px solid ${config.border}`,
        borderRadius: 3,
        cursor: reason ? 'help' : 'default',
      }}
    >
      <Gauge style={{ width: 10, height: 10 }} />
      {config.label}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// DROPDOWN SELECTOR
// ═══════════════════════════════════════════════════════════════════════════
interface DropdownProps {
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  label: string;
  value: string;
  width?: number;
}

export const Dropdown: React.FC<DropdownProps> = ({
  icon: Icon,
  label,
  value,
  width = 180,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '7px 12px',
        borderRadius: 6,
        width,
        backgroundColor: c.cardMuted,
        border: `1px solid ${c.border}`,
        textAlign: 'left',
      }}
    >
      <Icon style={{ width: 13, height: 13, color: c.accent }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 1 }}>{label}</div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: c.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// LENS CHIP
// ═══════════════════════════════════════════════════════════════════════════
interface LensChipProps {
  label: string;
  isActive: boolean;
}

export const LensChip: React.FC<LensChipProps> = ({ label, isActive }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '5px 10px',
        borderRadius: 16,
        backgroundColor: isActive ? c.accent : c.card,
        border: `1px solid ${isActive ? c.accent : c.border}`,
        fontSize: 12,
        fontWeight: 500,
        color: isActive ? '#fff' : c.textSecondary,
      }}
    >
      {label}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// RISK RADAR TILE
// ═══════════════════════════════════════════════════════════════════════════
interface RiskRadarTileProps {
  tile: ExecutionCockpitDailyBriefRiskTileDto;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  isActive: boolean;
  onClick: () => void;
}

export const RiskRadarTile: React.FC<RiskRadarTileProps> = ({
  tile,
  icon: Icon,
  isActive,
  onClick,
}) => {
  const severity = severityColors[tile.severity] ?? severityColors.info;

  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 14px',
        backgroundColor: isActive ? severity.activeBg : c.card,
        border: `1px solid ${isActive ? severity.activeBg : c.border}`,
        borderRadius: 8,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.15s ease',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon style={{ width: 14, height: 14, color: isActive ? '#fff' : severity.accent }} />
        <span
          style={{
            fontSize: 26,
            fontWeight: 700,
            fontFamily: 'Georgia, serif',
            color: isActive ? '#fff' : tile.count > 0 ? severity.accent : c.textMuted,
          }}
        >
          {tile.count}
        </span>
        <DeltaBadge value={tile.delta} />
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: isActive ? 'rgba(255,255,255,0.95)' : c.text,
          marginBottom: 4,
        }}
      >
        {tile.label}
      </div>
      <div
        style={{
          fontSize: 11,
          color: isActive ? 'rgba(255,255,255,0.75)' : c.textSecondary,
          lineHeight: 1.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {tile.topDriver}
      </div>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// DECISION CARD
// ═══════════════════════════════════════════════════════════════════════════
interface DecisionCardProps {
  decision: ExecutionCockpitDailyBriefDecisionDto;
  priorityDrivers?: ExecutionCockpitDailyBriefPriorityDriverDto[];
  isActive: boolean;
  onClick: () => void;
}

export const DecisionCard: React.FC<DecisionCardProps> = ({ decision, priorityDrivers, isActive, onClick }) => {
  const urgency = urgencyColors[decision.urgency] ?? urgencyColors.medium;
  const borderColor = isActive ? c.accentBorder : c.border;

  const whyNow = Array.isArray(priorityDrivers)
    ? priorityDrivers
        .filter((d) => String(d?.label ?? '').trim() && String(d?.value ?? '').trim())
        .slice(0, 3)
        .map((d) => `${d.label}: ${d.value}`)
        .join(' • ')
    : '';

  const whyNowTitle = Array.isArray(priorityDrivers)
    ? priorityDrivers
        .filter((d) => String(d?.label ?? '').trim() && String(d?.value ?? '').trim())
        .map((d) => {
          const note = String(d?.note ?? '').trim();
          return note ? `${d.label}: ${d.value} — ${note}` : `${d.label}: ${d.value}`;
        })
        .join('\n')
    : '';

  return (
    <div
      data-testid="decision-docket-card"
      onClick={onClick}
      style={{
        padding: '12px 14px',
        backgroundColor: isActive ? c.accentLight : c.card,
        borderWidth: 1,
        borderStyle: 'solid',
        borderTopColor: borderColor,
        borderRightColor: borderColor,
        borderBottomColor: borderColor,
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
        borderLeftColor: urgency.text,
        borderRadius: '0 8px 8px 0',
        cursor: 'pointer',
        marginBottom: 8,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'Georgia, serif',
          color: c.text,
          lineHeight: 1.5,
          marginBottom: 8,
        }}
      >
        {decision.question}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
        {decision.tradeoffs.map((t, i) => (
          <span
            key={i}
            style={{
              fontSize: 11,
              padding: '2px 6px',
              borderRadius: 3,
              backgroundColor: c.cardMuted,
              color: c.textSecondary,
            }}
          >
            {t}
          </span>
        ))}
      </div>
      {whyNow ? (
        <div
          title={whyNowTitle || undefined}
          style={{ fontSize: 11, color: c.textSecondary, marginBottom: 6, lineHeight: 1.4 }}
        >
          <span style={{ fontWeight: 700, color: c.textMuted }}>Why now:</span> {whyNow}
        </div>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, color: c.textMuted }}>{decision.linkedBet}</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: urgency.text,
            padding: '2px 6px',
            backgroundColor: urgency.bg,
            borderRadius: 3,
          }}
        >
          {decision.dueIn}
        </span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PRIORITY DRIVER CHIP
// ═══════════════════════════════════════════════════════════════════════════
interface PriorityDriverChipProps {
  driver: ExecutionCockpitDailyBriefPriorityDriverDto;
}

export const PriorityDriverChip: React.FC<PriorityDriverChipProps> = ({ driver }) => {
  const basis = driver.basis ?? 'derived';
  const theme =
    basis === 'assumption'
      ? { bg: c.amberLight, border: c.amberBorder, text: c.amber }
      : basis === 'configuration'
        ? { bg: c.blueLight, border: c.blueBorder, text: c.blue }
        : { bg: c.cardMuted, border: c.border, text: c.textSecondary };

  const titleParts = [String(driver.note ?? '').trim()].filter(Boolean);
  titleParts.push(`Basis: ${basis}`);

  return (
    <span
      title={titleParts.join(' • ')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        padding: '2px 8px',
        borderRadius: 999,
        backgroundColor: theme.bg,
        border: `1px solid ${theme.border}`,
        color: theme.text,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontWeight: 700 }}>{driver.label}</span>
      <span style={{ color: c.text }}> {driver.value}</span>
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// BET CARD
// ═══════════════════════════════════════════════════════════════════════════
interface BetCardProps {
  bet: ExecutionCockpitDailyBriefBetDto;
  isSelected: boolean;
  onClick: () => void;
}

export const BetCard: React.FC<BetCardProps> = ({ bet, isSelected, onClick }) => {
  const timeRatio = bet.weeksNeeded / bet.weeksLeft;
  const timeStatus = timeRatio > 1 ? 'red' : timeRatio > 0.8 ? 'amber' : 'green';
  const timeColor = { red: c.red, amber: c.amber, green: c.green }[timeStatus];
  const borderColor = isSelected ? c.accentBorder : c.border;

  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: isSelected ? c.accentLight : c.card,
        borderWidth: 1,
        borderStyle: 'solid',
        borderTopColor: borderColor,
        borderRightColor: borderColor,
        borderBottomColor: borderColor,
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
        borderLeftColor: ragColors[bet.rag]?.dot ?? c.textMuted,
        borderRadius: '0 8px 8px 0',
        padding: 14,
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <RAGDot status={bet.rag} size={8} />
            <span
              style={{ fontSize: 13, fontWeight: 600, fontFamily: 'Georgia, serif', color: c.text }}
            >
              {bet.name}
            </span>
          </div>
          <div style={{ fontSize: 11, color: c.textMuted }}>
            {formatExecutiveAccountabilityLabel(bet.accountability)}
          </div>
        </div>
        <ConfidenceBadge level={bet.confidence} reason={bet.confidenceReason} />
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 6 }}>
        <span
          style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Georgia, serif', color: timeColor }}
        >
          {bet.weeksNeeded}w
        </span>
        <span style={{ fontSize: 11, color: c.textMuted }}>need</span>
        <span style={{ fontSize: 11, color: c.textMuted }}>/</span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'Georgia, serif',
            color: c.textSecondary,
          }}
        >
          {bet.weeksLeft}w
        </span>
        <span style={{ fontSize: 11, color: c.textMuted }}>left</span>
        {timeRatio > 1 && (
          <span style={{ fontSize: 11, fontWeight: 600, color: c.red }}>
            +{Math.round((timeRatio - 1) * bet.weeksLeft)}w over
          </span>
        )}
      </div>

      <div
        style={{ height: 3, backgroundColor: c.cardMuted, borderRadius: 2, marginBottom: 10 }}
      >
        <div
          style={{
            height: '100%',
            width: `${Math.min(timeRatio * 100, 100)}%`,
            backgroundColor: timeColor,
            borderRadius: 2,
          }}
        />
      </div>

      <div
        style={{
          padding: '8px 10px',
          borderRadius: 6,
          marginTop: 'auto',
          backgroundColor: bet.rag === 'green' ? c.greenLight : c.cardMuted,
          border: `1px solid ${bet.rag === 'green' ? c.greenBorder : c.border}`,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: c.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: 2,
          }}
        >
          Binding Constraint
        </div>
        <div style={{ fontSize: 12, fontWeight: 500, color: c.text, lineHeight: 1.5 }}>
          {bet.constraint}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// DECISION OPTION BUTTON
// ═══════════════════════════════════════════════════════════════════════════
interface DecisionOptionProps {
  option: ExecutionCockpitDailyBriefOptionDto;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  isPrimary: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  isDisabled?: boolean;
}

export const DecisionOptionBtn: React.FC<DecisionOptionProps> = ({
  option,
  icon: Icon,
  isPrimary,
  isSelected = false,
  onSelect,
  isDisabled,
}) => {
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    borderRadius: 8,
    width: '100%',
    backgroundColor: isPrimary ? c.accent : c.card,
    border: `1px solid ${isPrimary ? c.accent : c.border}`,
    textAlign: 'left',
    marginBottom: 8,
    boxShadow: isSelected ? `0 0 0 2px ${c.accentBorder}` : undefined,
  };

  const content = (
    <>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 6,
          backgroundColor: isPrimary ? 'rgba(255,255,255,0.2)' : c.cardMuted,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon style={{ width: 16, height: 16, color: isPrimary ? '#fff' : c.textSecondary }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: isPrimary ? '#fff' : c.text }}>
          {option.action}
        </div>
        <div
          style={{
            fontSize: 12,
            color: isPrimary ? 'rgba(255,255,255,0.8)' : c.textSecondary,
            marginTop: 2,
          }}
        >
          {option.delta}
        </div>
      </div>
      {isSelected ? (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: 4,
            color: isPrimary ? '#fff' : c.accent,
            border: `1px solid ${isPrimary ? 'rgba(255,255,255,0.35)' : c.accentBorder}`,
            backgroundColor: isPrimary ? 'rgba(255,255,255,0.14)' : c.cardMuted,
            flexShrink: 0,
          }}
        >
          Selected
        </div>
      ) : null}
    </>
  );

  if (onSelect) {
    return (
      <button
        type="button"
        onClick={onSelect}
        disabled={isDisabled === true}
        aria-pressed={isSelected}
        style={{
          ...containerStyle,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.7 : 1,
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <div style={containerStyle}>{content}</div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PROOF ANCHOR
// ═══════════════════════════════════════════════════════════════════════════
interface ProofAnchorProps {
  proof: ExecutionCockpitDailyBriefProofDto;
}

export const ProofAnchor: React.FC<ProofAnchorProps> = ({ proof }) => {
  const items = Array.isArray(proof.items) ? proof.items : [];
  const canExpand = !proof.href && items.length > 1;
  const [isExpanded, setIsExpanded] = React.useState(false);

  const basisConfig =
    proof.basis === 'evidence'
      ? {
          label: proof.href ? 'Evidence-linked' : 'Evidence',
          bg: c.greenLight,
          border: c.greenBorder,
          color: c.green,
          title: 'Backed by a source snapshot.',
        }
      : proof.basis === 'heuristic'
        ? {
            label: 'Heuristic',
            bg: c.amberLight,
            border: c.amberBorder,
            color: c.amber,
            title: 'Heuristic inference; validate with evidence where possible.',
          }
        : {
            label: 'Assumption',
            bg: c.card,
            border: c.border,
            color: c.textMuted,
            title: 'Assumption; confirm before acting.',
          };

  const wrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '10px 12px',
    backgroundColor: c.cardMuted,
    borderRadius: 6,
    marginBottom: 6,
    textDecoration: 'none',
  };

  const content = (
    <>
      <CircleDot
        style={{ width: 12, height: 12, color: c.textMuted, marginTop: 2, flexShrink: 0 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: c.text }}>{proof.signal}</div>
        <div style={{ fontSize: 11, color: c.textSecondary, marginTop: 2, lineHeight: 1.5 }}>
          {proof.detail}
        </div>
        {canExpand ? (
          <div style={{ marginTop: 6 }}>
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              aria-expanded={isExpanded}
              style={{
                padding: 0,
                background: 'transparent',
                border: 0,
                color: c.textMuted,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 2,
              }}
            >
              {isExpanded ? 'Hide details' : `View all (${items.length})`}
            </button>

            {isExpanded ? (
              <ul
                style={{
                  margin: '6px 0 0 0',
                  paddingLeft: 16,
                  display: 'grid',
                  gap: 4,
                  fontSize: 11,
                  color: c.textSecondary,
                  lineHeight: 1.5,
                  maxHeight: 140,
                  overflowY: 'auto',
                }}
              >
                {items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <span
          title={basisConfig.title}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: 11,
            fontWeight: 600,
            color: basisConfig.color,
            padding: '2px 6px',
            backgroundColor: basisConfig.bg,
            border: `1px solid ${basisConfig.border}`,
            borderRadius: 3,
            cursor: 'help',
          }}
        >
          {basisConfig.label}
        </span>
        <ConfidenceBadge level={proof.confidence} reason={proof.confidenceReason} />
      </div>
    </>
  );

  const safeHref = sanitizeExternalUrl(proof.href ?? null);

  if (safeHref) {
    return (
      <a href={safeHref} target="_blank" rel="noreferrer" style={wrapperStyle}>
        {content}
      </a>
    );
  }

  return (
    <div style={wrapperStyle}>
      {content}
    </div>
  );
};
